import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { useAuth, useUser } from "@clerk/react";
import { ConvexError } from "convex/values";
import { api } from "../../convex/_generated/api";
import { Screen } from "../components/Screen";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { copy } from "../lib/copy";
import { clearPendingInvite, pendingInvite } from "../lib/invite";

/**
 * Signed in, but in no household yet.
 *
 * This is the screen that makes the deployed URL safe to know. Before auth,
 * opening it was joining; now opening it gets you here, and the only way
 * through is a code from somebody already inside.
 *
 * Arriving from an invite link, nobody should see this screen do anything: the
 * code was captured out of the URL at page load (see `lib/invite.ts`, and the
 * two places it had been getting lost), so this fills the field in and submits
 * it. Email, then the code from the mail, then in — the household code is the
 * link's job, not the guest's.
 *
 * It is the same screen and the same submit either way, which is why the auto
 * path needs no state of its own beyond "did we already try". A failure lands
 * exactly where a mistyped code lands: this form, with the value still in it
 * and the error under it.
 */
/**
 * The backend answers wrong, expired and already-used identically, on purpose —
 * telling them apart tells a guesser which of their guesses was a real code.
 * Shared by the typed path and the link path so they cannot drift.
 */
function joinError(e: unknown) {
  return e instanceof ConvexError ? String(e.data) : copy.join.invalid;
}

export function JoinScreen() {
  const join = useMutation(api.households.join);
  const { signOut } = useAuth();
  // The name from sign-up, carried the same way `bootstrap` gets it. Everyone
  // who is not the first person in the household arrives through this screen,
  // so leaving it out here would mean only the owner is ever named correctly.
  // `isLoaded` is load-bearing for the same reason it is in SessionProvider:
  // joining before it resolves writes the row with the email address, and
  // nothing in the app comes back to correct it.
  const { user, isLoaded: userLoaded } = useUser();

  /**
   * The code the link carried, read once. Held separately from the field so the
   * effect below has something stable to depend on — the field is free to be
   * edited, and re-firing the auto-join at each keystroke is exactly what must
   * not happen.
   */
  const [linkCode] = useState(pendingInvite);

  const [code, setCode] = useState(() => linkCode ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const value = code.trim();
    if (!value || pending) return;

    setPending(true);
    setError(null);
    try {
      await join({ code: value, displayName: user?.firstName ?? undefined });
      // Nothing to navigate to: `households.me` turns non-null and the gate in
      // __root swaps this screen for the app.
    } catch (e) {
      setError(joinError(e));
    } finally {
      setPending(false);
    }
  }

  /**
   * Fires once, and only for a code that came from a link — a code typed by
   * hand is submitted by the button, and auto-submitting a half-typed one would
   * spend the attempt on it.
   *
   * The guard is a ref rather than the `pending` flag: `useMutation` is not a
   * stable reference and StrictMode invokes effects twice, so a single attempt
   * has to be enforced rather than hoped for.
   *
   * The mutation is called here rather than through `submit` so this effect's
   * dependencies can be the real ones. `submit` is redefined every render and
   * the project has no `useCallback` to pin it with — React Compiler owns
   * memoization here — so depending on it would be depending on nothing.
   */
  const attempted = useRef(false);

  useEffect(() => {
    if (!linkCode || !userLoaded || attempted.current) return;
    attempted.current = true;
    // Spent at the attempt, not at the success. The code is single-use either
    // way, and clearing on success only would leave a reload retrying a dead
    // code forever. The field keeps the value, so a retry is still one tap.
    clearPendingInvite();
    setPending(true);
    void join({ code: linkCode, displayName: user?.firstName ?? undefined })
      .catch((e: unknown) => setError(joinError(e)))
      .finally(() => setPending(false));
  }, [linkCode, userLoaded, user, join]);

  return (
    <Screen
      title={copy.join.title}
      tabBar={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="mb-6 text-body text-muted">{linkCode ? copy.join.fromLink : copy.join.body}</p>

        <Field label={copy.join.code} variant="plain">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={copy.join.codePlaceholder}
            // The alphabet is uppercase and unambiguous; the backend
            // normalises anyway, so this is just so it reads back correctly.
            autoCapitalize="characters"
            autoCorrect="off"
            enterKeyHint="done"
            // Editing the value mid-flight would leave the field disagreeing
            // with what is being checked. This is also the whole of the auto
            // path's loading state — one in-flight shape, not a second one.
            disabled={pending}
            className="uppercase"
          />
        </Field>

        {error && <p className="mt-3 text-meta text-alert">{error}</p>}

        <Button type="submit" block disabled={!code.trim() || pending} className="mt-6">
          {copy.join.action}
        </Button>

        {/* The way out for someone who signed in with the wrong address.
            Without this the screen is a dead end with no back. */}
        <Button variant="quiet" onClick={() => void signOut()} className="mt-4">
          {copy.people.signOut}
        </Button>
      </form>
    </Screen>
  );
}
